import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Sparkles, Shirt, Sparkle } from "lucide-react";
import Shopping from "@/pages/Shopping";
import ShopForYou from "@/pages/beauty/ShopForYou";
import { useAuth } from "@/lib/AuthProvider";
import { showBanner, hideBanner } from "@/lib/admob";

const TABS = [
  { id: "wearables", label: "Wearables", icon: Shirt, sub: "Clothes + accessories tuned to your closet gaps" },
  { id: "bodycare",  label: "Body Care", icon: Sparkle, sub: "Personalised beauty picks from your latest scan" },
];

// Legacy deep-links: /shop?tab=curated | /shop?tab=foryou | /shop?tab=recommendations
// now resolve to the closest new tab so existing bookmarks keep working.
const TAB_ALIASES = {
  foryou: "wearables",
  recommendations: "wearables",
  curated: "wearables",
  beauty: "bodycare",
};
const VALID_TABS = new Set(TABS.map((t) => t.id));

export default function Shop() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const rawTab = params.get("tab");
  const resolvedTab = TAB_ALIASES[rawTab] || rawTab;
  const initialTab = VALID_TABS.has(resolvedTab) ? resolvedTab : "wearables";
  const [tab, setTab] = useState(initialTab);

  // Free users see an adaptive bottom banner; premium hides it. Cleans up on
  // unmount so it doesn't leak into other pages.
  const isFree = !user?.subscription_tier || user.subscription_tier === "free";
  useEffect(() => {
    if (!isFree) return undefined;
    showBanner();
    return () => { hideBanner(); };
  }, [isFree]);

  const onTab = (t) => {
    setTab(t);
    params.set("tab", t);
    setParams(params, { replace: true });
  };

  return (
    <div className="overflow-x-hidden">
      <div className="border-b border-border bg-background sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-5 sm:pt-6">
          <p className="eyebrow flex items-center gap-2"><Sparkles size={12} /> Shop</p>
          <h1 className="editorial-h1 mt-2 sm:mt-3">Shop the look.</h1>
          <div className="mt-4 sm:mt-6 flex gap-1 -mb-px overflow-x-auto scrollbar-none" data-testid="shop-tabs">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onTab(t.id)}
                  data-testid={`shop-tab-${t.id}`}
                  className={`flex items-center gap-2 px-3 sm:px-5 h-11 sm:h-12 border-b-2 text-[11px] sm:text-[12px] uppercase tracking-[0.14em] sm:tracking-[0.18em] whitespace-nowrap transition-colors ${
                    active ? "border-accent text-accent font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Icon size={14} /> {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div data-testid={`shop-pane-${tab}`}>
        {tab === "wearables" && <Shopping />}
        {tab === "bodycare" && <ShopForYou />}
      </div>
    </div>
  );
}
